import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Container,
  Divider,
  Typography,
  useMediaQuery,
  Grid,
  CardHeader,
  Tooltip,
  IconButton,
} from '@mui/material';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import SettingsIcon from '@mui/icons-material/Settings';

import CreateProjectDialog from './CreateProjectDialog';
import DeleteProjectDialog from './DeleteProjectDialog';
import ProjectUserAccessDialog from './ProjectUserAccessDialog';

import { logos } from '../../utils/logos';
import { ORGANIZATION_SERVER } from '../../Config';
import axios from 'axios';

axios.defaults.withCredentials = true;

const OrganizationPage = () => {
  const [groupList, setGroupList] = useState([]); // groupList는 배열로 초기화
  const [open, setOpen] = useState(false);
  const [projectId, setProjectId] = useState('');
  const [projectName, setProjectName] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [permissionsOpen, setPermissionsOpen] = useState(false);

  const user = useSelector((state) => state.user);

  const matches = useMediaQuery('(max-width:768px)');
  const navigate = useNavigate();

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleClickDeleteOpen = (e) => {
    let data = e.target.getAttribute('data');
    if (data === null) data = e.target.parentNode.getAttribute('data');

    setProjectId(data);
    setDeleteModalOpen(true);
    setProjectName(deleteTextParsing(e));
  };

  const handleDeleteClose = () => {
    setDeleteModalOpen(false);
    setProjectId('');
  };

  const handleClickPermissionOpen = (e) => {
    let data = e.target.getAttribute('data');
    if (data === null) data = e.target.parentNode.getAttribute('data');

    setProjectId(data);
    setPermissionsOpen(true);
  };

  const handleClickPermissionClose = () => {
    setPermissionsOpen(false);
  };

  const deleteTextParsing = (e) => {
    let data = e.target.getAttribute('index');
    if (data === null) data = e.target.parentNode.getAttribute('index');
    let projectname = groupList[data].projectName;
    return projectname.replace(/(\s*)/g, '');
  };

  // 데이터 로드 함수
  const loadProjects = () => {
    axios.get(`${process.env.REACT_APP_SERVER}${ORGANIZATION_SERVER}/group/list`).then((res) => {
      setGroupList(Array.isArray(res.data) ? res.data : []); // 항상 배열 보장
    });
  };

  const canShowPermissionButton = (v) => {
    if (!user.userData) return false;
    if (user.userData.role === 'user') return false;
    if (user.userData.role === 'super admin') return true;
    if (user.userData.role === 'admin') {
      return v._id === user.userData.group;
    }
    return false;
  };

  const modifyImage = (v) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      if (!file.type.startsWith('image/')) {
        alert('이미지 파일만 업로드 가능합니다.');
        return;
      }

      const maxSizeInBytes = 3 * 1024 * 1024;
      if (file.size > maxSizeInBytes) {
        alert('이미지는 3MB 이하로 업로드해 주세요.');
        return;
      }

      const fd = new FormData();
      fd.append('id', v._id);
      fd.append('image', file);

      try {
        const res = await axios.post(`${process.env.REACT_APP_SERVER}${ORGANIZATION_SERVER}/update-image`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        if (res.status === 200) {
          loadProjects(); // 새로고침
        }
      } catch (err) {
        alert('이미지 업로드에 실패했습니다.');
      }
    };
    input.click();
  };

  useEffect(() => {
    loadProjects();
  }, []);

  return (
    <div className="OrganizationPage">
      <Container
        maxWidth="xl"
        sx={matches ? { paddingTop: '116px', paddingBottom: '100px' } : { paddingTop: '188px', paddingBottom: '100px' }}
      >
        <Grid container spacing={2}>
          {user.userData.role === 'super admin' && (
            <Grid size={{ xs: 12 }}>
              <div style={{ float: 'right' }}>
                <Button color="primary" variant="contained" onClick={handleClickOpen} sx={{ textTransform: 'none' }}>
                  <NoteAddIcon />
                  Project Create
                </Button>
              </div>
            </Grid>
          )}
          {groupList.length > 0 ? (
            groupList.map((v, i) => (
              <Grid
                key={v._id}
                size={{
                  xs: 12,
                  md: 6,
                  lg: 4,
                  xl: 3,
                }}
              >
                <Card sx={{ width: 345 }} elevation={3}>
                  <CardHeader
                    action={
                      <>
                        {user.userData && canShowPermissionButton(v) && (
                          <Tooltip title="권한설정" placement="top">
                            <IconButton
                              data={v._id}
                              index={i}
                              size="small"
                              onClick={handleClickPermissionOpen}
                              sx={{ padding: '4px', mr: 1 }}
                            >
                              <SettingsIcon data={v._id} index={i} color="inherit" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {user.userData.role === 'super admin' && (
                          <Tooltip title="프로젝트 삭제" placement="top">
                            <IconButton
                              data={v._id}
                              index={i}
                              onClick={handleClickDeleteOpen}
                              size="small"
                              sx={{ padding: '4px' }}
                            >
                              <HighlightOffIcon data={v._id} index={i} color="inherit" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </>
                    }
                    sx={{ padding: 1, height: '40px' }}
                  />
                  <div
                    style={{
                      width: '100%',
                      height: 220,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      backgroundColor: 'white',
                      padding: '12px 16px',
                      boxSizing: 'border-box',
                      cursor: user.userData.role === 'super admin' ? 'pointer' : 'default',
                      border: v.image || logos[v._id] ? 'none' : '1px dashed #ccc',
                    }}
                    onClick={() => {
                      if (user.userData.role === 'super admin') {
                        modifyImage(v);
                      }
                    }}
                  >
                    {v.image ? (
                      <CardMedia
                        component="img"
                        src={`data:image/png;base64,${v.image}`}
                        sx={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          width: 'auto',
                          height: 'auto',
                          objectFit: 'contain',
                          objectPosition: 'center',
                          imageRendering: 'auto',
                        }}
                      />
                    ) : logos[v._id] ? (
                      <img
                        src={logos[v._id]}
                        alt="logo"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          width: 'auto',
                          height: 'auto',
                          objectFit: 'contain',
                          imageRendering: 'auto',
                        }}
                      />
                    ) : (
                      <div style={{ textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          No image
                        </Typography>
                        {user.userData.role === 'super admin' && (
                          <Typography variant="caption" color="primary">
                            Click + to upload image
                          </Typography>
                        )}
                      </div>
                    )}
                  </div>

                  <Divider />
                  <CardContent sx={{ height: 90 }}>
                    <Typography gutterBottom variant="h6" component="div">
                      {v.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {v.projectName}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ width: '100%', justifyContent: 'space-between' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      color="primary"
                      onClick={() => {
                        navigate('/Project', { state: { group: v._id } });
                      }}
                      style={{ textTransform: 'none' }}
                    >
                      More
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      color="primary"
                      onClick={() => {
                        navigate(`/herbaladr?groupId=${v._id}`);
                      }}
                      style={{ textTransform: 'none', marginRight: '16px' }}
                    >
                      HerbReport
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))
          ) : (
            <Typography variant="body2" sx={{ mt: 2 }}>
              No projects found.
            </Typography>
          )}
        </Grid>
      </Container>
      <CreateProjectDialog open={open} handleClose={handleClose} projectLoad={loadProjects} />
      <DeleteProjectDialog
        open={deleteModalOpen}
        projectId={projectId}
        handleClose={handleDeleteClose}
        projectLoad={loadProjects}
        deleteText={projectName}
      />
      <ProjectUserAccessDialog
        open={permissionsOpen}
        handleClose={handleClickPermissionClose}
        projectId={projectId}
        // group={v.group} // group: user id 배열
        // userList={allUsers}       // API로 받아온 전체 사용자 [{_id, name, ...}]
        // onUpdate={(updatedGroup) => {
        //   // 예: 해당 row의 group 상태만 업데이트
        // }}
      />
    </div>
  );
};

export default OrganizationPage;
